import type {
  CannedResponse,
  CreateCannedResponse,
  Page,
  UpdateCannedResponse,
} from '@servicetim/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { adminPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const base = adminPath('/canned-responses');
const projectPath = (projectId: string) => adminPath(`/projects/${projectId}/canned-responses`);

/**
 * `GET /admin/v1/canned-responses?projectId=` — tenant-wide (no `projectId`) or the project's
 * plus tenant-wide ones. `GET /admin/v1/projects/{id}/canned-responses` is the same list scoped
 * through the project router (agents without tenant admin rights use that one).
 */
export function useCannedResponses(
  options: { projectId?: string; viaProject?: boolean; pageSize?: number } = {},
) {
  const { request, keys } = useApiContext();
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const { projectId, viaProject = true } = options;
  const path = projectId && viaProject ? projectPath(projectId) : base;
  return usePagedList<CannedResponse>({
    queryKey: projectId
      ? keys.project(projectId).cannedResponses({ pageSize, viaProject })
      : keys.cannedResponses.list({ pageSize }),
    fetchPage: (cursor, signal) =>
      request<Page<CannedResponse>>('GET', path, {
        query: {
          projectId: projectId && !viaProject ? projectId : undefined,
          ...pageQuery(cursor, pageSize),
        },
        signal,
      }),
  });
}

export function useCannedResponse(cannedResponseId: string | undefined) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.cannedResponses.detail(cannedResponseId ?? ''),
    queryFn: ({ signal }) =>
      request<CannedResponse>('GET', `${base}/${cannedResponseId}`, { signal }),
    enabled: cannedResponseId !== undefined,
  });
}

/** `POST /admin/v1/canned-responses` (tenant admin) or `POST .../projects/{id}/canned-responses`. */
export function useCreateCannedResponse() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ viaProject, ...input }: CreateCannedResponse & { viaProject?: boolean }) =>
      request<CannedResponse>(
        'POST',
        viaProject && input.projectId ? projectPath(input.projectId) : base,
        { body: input },
      ),
    onSuccess: (created) =>
      invalidate(
        keys.cannedResponses.all,
        ...(created.projectId ? [keys.project(created.projectId).scope] : []),
      ),
  });
}

export function useUpdateCannedResponse() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateCannedResponse }) =>
      request<CannedResponse>('PATCH', `${base}/${id}`, { body: patch }),
    onSuccess: (updated) =>
      invalidate(
        keys.cannedResponses.all,
        ...(updated.projectId ? [keys.project(updated.projectId).scope] : []),
      ),
  });
}

export function useDeleteCannedResponse() {
  const { request, keys, invalidate } = useApiContext();
  return useMutation({
    mutationFn: ({ id, projectId }: { id: string; projectId?: string | null }) =>
      request<unknown>('DELETE', `${base}/${id}`).then(() => projectId),
    onSuccess: (projectId) =>
      invalidate(keys.cannedResponses.all, ...(projectId ? [keys.project(projectId).scope] : [])),
  });
}
